import React, { useEffect, useRef, useState } from "react";
import type { ManifestAsset, ManifestTaxonomy } from "../../types.js";
import { getIdToken } from "../../admin/auth.js";

interface Props {
  taxonomy: ManifestTaxonomy;
  /** Existing album paths, for the folder datalist. */
  albumPaths: string[];
  onClose: () => void;
  onAdded: (asset: ManifestAsset) => void;
}

type Phase = "idle" | "uploading" | "done" | "error";

interface MetaFields {
  name: string;
  sku: string;
  region: string;
  department: string;
  productCategory: string;
  assetType: string;
  albumPath: string;
}

const EMPTY: MetaFields = {
  name: "",
  sku: "",
  region: "",
  department: "",
  productCategory: "",
  assetType: "",
  albumPath: "",
};

// Videos run well past the old 60 MB image ceiling.
const MAX_BYTES = 250 * 1024 * 1024;

// Cloudflare rejects request bodies over ~100 MB and the origin nginx caps
// /admin/ at 64 MB, so anything bigger than DIRECT_LIMIT goes up in CHUNK_BYTES
// slices via an upload session (POST /admin/upload → PUT chunks → finalize).
const DIRECT_LIMIT = 32 * 1024 * 1024;
const CHUNK_BYTES = 16 * 1024 * 1024;

// File input filter + which kinds we accept. Mirrors the server's
// detectSchemeFromFilename: images get a resize ladder; video and vector/doc
// files (eps/ai/pdf) are stored as-is. eps/ai often carry no useful MIME type,
// so we classify by extension, not file.type.
const ACCEPT = ".jpg,.jpeg,.png,.webp,.gif,.mp4,.mov,.webm,.m4v,.eps,.ai,.pdf,image/*,video/*";

function uploadKind(name: string): "image" | "video" | "document" | null {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  if (["jpg", "jpeg", "png", "webp", "gif"].includes(ext)) return "image";
  if (["mp4", "mov", "webm", "m4v"].includes(ext)) return "video";
  if (["eps", "ai", "pdf"].includes(ext)) return "document";
  return null;
}

export function AddAssetModal({ taxonomy, albumPaths, onClose, onAdded }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fields, setFields] = useState<MetaFields>(EMPTY);
  const [phase, setPhase] = useState<Phase>("idle");
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragover, setDragover] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const busy = phase === "uploading";

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  // Revoke the object URL when the preview changes or the modal unmounts.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  function chooseFile(f: File | undefined | null) {
    if (!f) return;
    const kind = uploadKind(f.name);
    if (!kind) {
      setError("Unsupported file type. Allowed: images (JPEG, PNG, WebP, GIF), video (MP4, MOV, WebM), and EPS, AI, or PDF.");
      return;
    }
    if (f.size > MAX_BYTES) {
      setError(`File is larger than ${MAX_BYTES / 1024 / 1024} MB.`);
      return;
    }
    setError(null);
    setFile(f);
    // Only raster images can be shown in an <img>; video/eps/ai/pdf get a chip.
    setPreviewUrl((old) => {
      if (old) URL.revokeObjectURL(old);
      return kind === "image" ? URL.createObjectURL(f) : null;
    });
    // Default the name from the filename (sans extension) if the field is empty.
    setFields((prev) => (prev.name ? prev : { ...prev, name: stripExt(f.name) }));
  }

  function setField(k: keyof MetaFields, v: string) {
    setFields((prev) => ({ ...prev, [k]: v }));
  }

  async function submit() {
    if (!file) {
      setError("Choose a file first.");
      return;
    }
    setPhase("uploading");
    setError(null);
    setProgress(null);
    try {
      // Chrome silently invalidates a File handle if the on-disk file moved or
      // changed after it was picked (cloud-drive placeholders hydrating, a
      // re-export, …); every network read then dies as a bare "Failed to
      // fetch". Probe one byte up front so that case gets a message the user
      // can act on.
      try {
        await file.slice(0, 1).arrayBuffer();
      } catch {
        throw new Error(
          "Couldn't read the file from disk — it may have moved or changed since you selected it. " +
            "Click the file box and choose it again, then retry.",
        );
      }
      const meta = Object.fromEntries(
        Object.entries(fields).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v),
      );
      const headers: Record<string, string> = {
        "X-Filename": sanitizeHeader(file.name),
        "X-Asset-Metadata": utf8ToBase64(JSON.stringify(meta)),
      };
      let body: ArrayBuffer | undefined;
      if (file.size > DIRECT_LIMIT) {
        headers["X-Upload-Session"] = await uploadInChunks(file, setProgress);
      } else {
        headers["Content-Type"] = "application/octet-stream";
        body = await file.arrayBuffer();
      }
      // Fetch the token after the chunk uploads — a long upload can outlive it.
      headers.Authorization = `Bearer ${await getIdToken()}`;
      const res = await fetch("/admin/asset", { method: "POST", headers, body });
      const json = (await res.json().catch(() => ({}))) as { ok?: boolean; asset?: ManifestAsset; error?: string };
      if (!res.ok || !json.ok || !json.asset) {
        throw new Error(json.error || `Upload failed (${res.status})`);
      }
      onAdded(json.asset);
      setPhase("done");
    } catch (e) {
      const msg = (e as Error).message;
      // fetch() reports every network-layer death as "Failed to fetch" — for
      // an upload the two real-world causes are a stale File handle (see the
      // probe above; it can also go stale mid-upload) and a dropped connection.
      setError(
        /failed to fetch/i.test(msg)
          ? "The upload was interrupted before it finished. Re-choose the file and try again — " +
              "if it keeps happening, check your connection and let Steve know."
          : msg,
      );
      setPhase("error");
    } finally {
      setProgress(null);
    }
  }

  if (phase === "done") {
    return (
      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal add-asset-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
          <header>
            <h2>Asset added</h2>
            <button className="close" aria-label="Close" onClick={onClose}>×</button>
          </header>
          <div className="modal-body">
            <p className="add-done">
              <strong>{fields.name || file?.name}</strong> was added and is live in the library now.
            </p>
          </div>
          <footer className="bulk-footer">
            <button
              className="bulk-btn"
              onClick={() => {
                // Reset for another add without closing.
                setFile(null);
                setPreviewUrl((old) => {
                  if (old) URL.revokeObjectURL(old);
                  return null;
                });
                setFields(EMPTY);
                setPhase("idle");
              }}
            >
              Add another
            </button>
            <button className="bulk-btn bulk-btn-primary" onClick={onClose}>Done</button>
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="modal add-asset-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-busy={busy}>
        <header>
          <h2>Add asset</h2>
          <button className="close" aria-label="Close" onClick={onClose} disabled={busy}>×</button>
        </header>
        <div className="modal-body">
          <div
            className={`add-drop ${dragover ? "add-drop-over" : ""} ${previewUrl ? "add-drop-has" : ""}`}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragover(true);
            }}
            onDragLeave={() => setDragover(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragover(false);
              chooseFile(e.dataTransfer.files?.[0]);
            }}
          >
            {previewUrl ? (
              <img className="add-preview" src={previewUrl} alt="preview" />
            ) : file ? (
              <div className="add-drop-hint">
                <strong>{fileKindLabel(file.name)} selected</strong>
                <span>{file.name}</span>
              </div>
            ) : (
              <div className="add-drop-hint">
                <strong>Drop a file here</strong>
                <span>or click to browse — images, video (MP4/MOV/WebM), or EPS/AI/PDF, up to 250 MB</span>
              </div>
            )}
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              hidden
              onChange={(e) => chooseFile(e.target.files?.[0])}
            />
          </div>
          {file && (
            <p className="add-filemeta">
              {file.name} · {formatBytes(file.size)}
            </p>
          )}

          <div className="add-form">
            <label className="add-field add-field-wide">
              <span>Name</span>
              <input value={fields.name} onChange={(e) => setField("name", e.target.value)} placeholder="Display name" />
            </label>
            <label className="add-field">
              <span>SKU</span>
              <input value={fields.sku} onChange={(e) => setField("sku", e.target.value)} placeholder="optional" />
            </label>
            <SelectField label="Region" value={fields.region} options={taxonomy.regions} onChange={(v) => setField("region", v)} />
            <SelectField label="Department" value={fields.department} options={taxonomy.departments} onChange={(v) => setField("department", v)} />
            <SelectField label="Product Category" value={fields.productCategory} options={taxonomy.productCategories} onChange={(v) => setField("productCategory", v)} />
            <SelectField label="Asset Type" value={fields.assetType} options={taxonomy.assetTypes} onChange={(v) => setField("assetType", v)} />
            <label className="add-field add-field-wide">
              <span>Folder</span>
              <input
                value={fields.albumPath}
                onChange={(e) => setField("albumPath", e.target.value)}
                placeholder="e.g. Reusable Respirators / 8000 Series"
                list="add-album-paths"
              />
              <datalist id="add-album-paths">
                {albumPaths.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            </label>
          </div>

          {error && <p className="add-error">{error}</p>}
        </div>
        <footer className="bulk-footer">
          <button className="bulk-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="bulk-btn bulk-btn-primary" onClick={() => void submit()} disabled={busy || !file}>
            {busy ? (progress !== null ? `Uploading… ${Math.round(progress * 100)}%` : "Uploading…") : "Add to library"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="add-field">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">(none)</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Stage a large file into a server-side upload session, one chunk per request
 * (each small enough for Cloudflare and nginx). Returns the session id to pass
 * as X-Upload-Session on the finalizing POST /admin/asset.
 */
async function uploadInChunks(file: File, onProgress: (frac: number) => void): Promise<string> {
  const create = await fetch("/admin/upload", {
    method: "POST",
    headers: { Authorization: `Bearer ${await getIdToken()}` },
  });
  const created = (await create.json().catch(() => ({}))) as { ok?: boolean; id?: string; error?: string };
  if (!create.ok || !created.ok || !created.id) {
    throw new Error(created.error || `Upload failed (${create.status})`);
  }
  let index = 0;
  for (let off = 0; off < file.size; off += CHUNK_BYTES) {
    const chunk = file.slice(off, off + CHUNK_BYTES);
    const res = await fetch(`/admin/upload/${created.id}`, {
      method: "PUT",
      headers: {
        // Re-fetched per chunk: getIdToken caches, and refreshes if stale.
        Authorization: `Bearer ${await getIdToken()}`,
        "Content-Type": "application/octet-stream",
        "X-Chunk-Index": String(index),
      },
      body: chunk,
    });
    const json = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
    if (!res.ok || !json.ok) throw new Error(json.error || `Upload failed (${res.status})`);
    index += 1;
    onProgress(Math.min(1, (off + chunk.size) / file.size));
  }
  return created.id;
}

function fileKindLabel(name: string): string {
  const kind = uploadKind(name);
  return kind === "video" ? "Video" : kind === "document" ? "File" : "Image";
}

function stripExt(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(0, dot) : name;
}

function sanitizeHeader(s: string): string {
  // Header values must be ASCII; strip anything that isn't.
   
  return s.replace(/[^\x20-\x7E]/g, "_");
}

function utf8ToBase64(s: string): string {
  return btoa(unescape(encodeURIComponent(s)));
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
